import React from 'react';
import kvsLogoSvg from '../../assets/kvsLogo.svg';
import { CSP_NONCE } from '../../shared/services/cspNonce';

// Helper function to create animations dynamically
const createAnimation = (loaderName: string, frames: string, suffix: string): string => {
  const animationName = `kvs-loader-${loaderName}-${suffix}`;
  
  if (typeof window === "undefined" || !window.document) {
    return animationName;
  }
  
  const styleEl = document.createElement("style");
  // Production's CSP applies a <style> tag only with the page nonce (see cspNonce).
  if (CSP_NONCE) styleEl.setAttribute("nonce", CSP_NONCE);
  document.head.appendChild(styleEl);
  const styleSheet = styleEl.sheet;
  
  const keyFrames = `
    @keyframes ${animationName} {
      ${frames}
    }
  `;
  
  if (styleSheet) {
    styleSheet.insertRule(keyFrames, 0);
  }
  
  return animationName;
};

// Helper function to parse length and unit
const parseLengthAndUnit = (size: string | number): { value: number; unit: string } => {
  if (typeof size === "number") {
    return { value: size, unit: "px" };
  }
  
  const valueString = (size.match(/^[0-9.]*/) || "").toString();
  const value = valueString.includes(".")
    ? parseFloat(valueString)
    : parseInt(valueString, 10);
  const unit = (size.match(/[^0-9]*$/) || "").toString();
  
  const cssUnit: Record<string, boolean> = {
    cm: true, mm: true, in: true, px: true, pt: true, pc: true,
    em: true, ex: true, ch: true, rem: true, vw: true, vh: true,
    vmin: true, vmax: true, "%": true,
  };
  
  if (cssUnit[unit]) {
    return { value, unit };
  }
  
  console.warn(
    `KVS Loader: ${size} is not a valid css value. Defaulting to ${value}px.`
  );
  return { value, unit: "px" };
};

// Helper function to get CSS value
const cssValue = (value: string | number): string => {
  const lengthWithUnit = parseLengthAndUnit(value);
  return `${lengthWithUnit.value}${lengthWithUnit.unit}`;
};

// Create fade animation
const fadeAnimation = createAnimation(
  "KVSLoader",
  `50% { opacity: 0.3 } 100% { opacity: 1 }`,
  "fade"
);

interface KVSLoaderProps {
  loading?: boolean;
  size?: number | string;
  speedMultiplier?: number;
  color?: string;
  cssOverride?: React.CSSProperties;
  barWidth?: number;
  barHeight?: number;
}

const KVSLoader: React.FC<KVSLoaderProps> = ({
  loading = true,
  size = 80,
  speedMultiplier = 1,
  color = '#6B1786',
  cssOverride = {},
  barWidth = 4,
  barHeight = 16,
}) => {
  if (!loading) return null;

  const sizeValue = parseLengthAndUnit(size).value;
  const radius = sizeValue / 2;

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: cssValue(size),
    height: cssValue(size),
    ...cssOverride,
  };

  const logoWrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    height: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  };

  const logoStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };

  // Create 8 bars in radial pattern
  const bars = Array.from({ length: 8 }, (_, i) => {
    const angle = (i * 45 * Math.PI) / 180;
    const x = Math.cos(angle) * (radius - barHeight / 2);
    const y = Math.sin(angle) * (radius - barHeight / 2);

    const barStyle: React.CSSProperties = {
      position: 'absolute',
      width: `${barWidth}px`,
      height: `${barHeight}px`,
      backgroundColor: color,
      borderRadius: `${barWidth / 2}px`,
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${i * 45 + 90}deg)`,
      animation: `${fadeAnimation} ${1.2 / speedMultiplier}s ${i * 0.15}s infinite ease-in-out`,
      animationFillMode: 'both',
      zIndex: 1,
    };

    return <span key={i} style={barStyle} />;
  });

  return (
    <span style={containerStyle}>
      {bars}
      <div style={logoWrapperStyle}>
        <img 
          src={kvsLogoSvg} 
          alt="Loading..." 
          style={logoStyle}
        />
      </div>
    </span>
  );
};

export default KVSLoader;